import React, { useEffect } from "react";
import PropTypes from "prop-types";
import "./AssetSize.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  setAssetSize,
  setClearAll,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { useDebounce } from "primereact/hooks";
import { Skeleton } from "primereact/skeleton";

export const IN_MILLION = 1_000_000;
export const ASSET_SIZE_MIN_PLACEHOLDER = 0;
export const ASSET_SIZE_MAX_PLACEHOLDER = 5_000_000_000_000 / IN_MILLION;

const formatTwoDecimals = (val) => {
  if (val === null || val === undefined || val === "") return "";
  return Number(val).toFixed(2);
};

const DECIMAL_INPUT_PATTERN = /^\d*\.?\d{0,2}$/;

const getMinInitialValue = (selectedCohort, assetSize) => {
  const raw = selectedCohort?.filterJson?.assets?.min;
  if (raw == null) return assetSize?.minValue || "";
  return raw / IN_MILLION;
};

const getMaxInitialValue = (selectedCohort, assetSize) => {
  const raw = selectedCohort?.filterJson?.assets?.max;
  if (raw == null) return assetSize?.maxValue || "";
  return raw / IN_MILLION;
};

const validate = (minValue, maxValue, setMinError, setMaxError) => {
  if (minValue === "" && maxValue === "") {
    setMinError("");
    setMaxError("");
    return;
  }

  if (minValue && Number(minValue) < ASSET_SIZE_MIN_PLACEHOLDER) {
    setMinError(`Min should be ≥ ${ASSET_SIZE_MIN_PLACEHOLDER}`);
  } else if (Number(minValue) >= Number(maxValue || ASSET_SIZE_MAX_PLACEHOLDER)) {
    setMinError("Min cannot be greater than equal to Max");
  } else {
    setMinError("");
  }

  if (maxValue && Number(maxValue) > ASSET_SIZE_MAX_PLACEHOLDER) {
    setMaxError(`Max should be ≤ ${ASSET_SIZE_MAX_PLACEHOLDER}`);
  } else if (Number(maxValue || ASSET_SIZE_MAX_PLACEHOLDER) <= Number(minValue)) {
    setMaxError("Max cannot be less than equal to Min");
  } else {
    setMaxError("");
  }
};

const AssetSize = ({ minError, setMinError, maxError, setMaxError }) => {
  const dispatch = useDispatch();

  const assetSize = useSelector((state) => state?.cohortReducer?.assetSize);
  const selectedCohort = useSelector((state) => state?.cohortReducer?.selectedCohort);
  const clearAll = useSelector((state) => state?.cohortReducer?.clearAll);
  const isFieldsEditiable = useSelector((state) => state?.cohortReducer?.isFieldsEditiable);

  const [minValue, debouncedMinValue, setMinValue] = useDebounce("", 400);
  const [maxValue, debouncedMaxValue, setMaxValue] = useDebounce("", 400);

  useEffect(() => {
    dispatch(
      setAssetSize({
        ...assetSize,
        minValue: debouncedMinValue,
        maxValue: debouncedMaxValue,
      }),
    );
    validate(debouncedMinValue, debouncedMaxValue, setMinError, setMaxError);
  }, [debouncedMinValue, debouncedMaxValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMinValue(
      formatTwoDecimals(getMinInitialValue(selectedCohort, assetSize)),
    );
    setMaxValue(
      formatTwoDecimals(getMaxInitialValue(selectedCohort, assetSize)),
    );
  }, [selectedCohort?.filterJson?.assets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!clearAll) return;
    setMinValue("");
    setMaxValue("");
    const timer = setTimeout(() => {
      dispatch(setClearAll(false));
    }, 1000);
    return () => clearTimeout(timer);
  }, [clearAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMinChange = (e) => {
    if (DECIMAL_INPUT_PATTERN.test(e.target.value)) setMinValue(e.target.value);
  };

  const handleMaxChange = (e) => {
    if (DECIMAL_INPUT_PATTERN.test(e.target.value)) setMaxValue(e.target.value);
  };

  return (
    <div className="asset-size">
      {clearAll ? (
        <div className="input-container d-flex">
          <div className="input-field" style={{ flex: 1 }}>
            <Skeleton height="3rem" />
            <div className="error-container">
              {minError && <span className="error-text">{minError}</span>}
            </div>
          </div>
          <div
            className="input-field"
            style={{ alignSelf: "normal", marginTop: "1rem" }}
          >
            -
          </div>
          <div className="input-field" style={{ flex: 1 }}>
            <Skeleton height="3rem" />
            <div className="error-container">
              {minError && <span className="error-text">{minError}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="input-container">
          <div className="input-field">
            <div className="field">
              <span className="dollar-icon">$</span>
              <input
                id="asset-size-min"
                placeholder={`${ASSET_SIZE_MIN_PLACEHOLDER}.00`}
                value={minValue ?? ""}
                onChange={handleMinChange}
                disabled={!isFieldsEditiable}
              />
            </div>
            <div className="error-container">
              {minError && <span className="error-text">{minError}</span>}
            </div>
          </div>
          <div className="input-field">
            <div className="field">-</div>
          </div>
          <div className="input-field">
            <div className="field">
              <span className="dollar-icon">$</span>
              <input
                placeholder={`${ASSET_SIZE_MAX_PLACEHOLDER}.00`}
                value={maxValue ?? ""}
                onChange={handleMaxChange}
                disabled={!isFieldsEditiable}
              />
            </div>
            <div className="error-container">
              {maxError && <span className="error-text">{maxError}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AssetSize.propTypes = {
  minError: PropTypes.string.isRequired,
  setMinError: PropTypes.func.isRequired,
  maxError: PropTypes.string.isRequired,
  setMaxError: PropTypes.func.isRequired,
};

export default AssetSize;
